import React, { useState, useEffect } from 'react';
import { progressTracker, tagMasteryCalculator } from '@services';
import './BucketAnalytics.css';

/**
 * BucketAnalytics Component
 * Displays question mastery distribution across 4 buckets:
 * - Unseen: Score 0-33% (brand new questions)
 * - Weak: Score 34-66% (struggling with topic)
 * - Review: Score 67-89% (needs spaced repetition)
 * - Learned: Score 90-100% (mastered topic)
 * 
 * Shows both section-level and course-level breakdown
 * Implements HamTestOnline bucket visualization
 */
export function BucketAnalytics({ userId, courseId, sectionId = null }) {
  const [sectionBuckets, setSectionBuckets] = useState(null);
  const [courseBuckets, setCourseBuckets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tagMasteries, setTagMasteries] = useState([]);

  useEffect(() => {
    loadBucketData();
  }, [userId, courseId, sectionId]);

  const loadBucketData = async () => {
    if (!userId || !courseId) {
      setError('Missing userId or courseId');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get course-level buckets
      const courseBucketsData = await progressTracker.getCourseBucketBreakdown(userId, courseId);
      setCourseBuckets(courseBucketsData);

      // Get section-level buckets if sectionId provided
      if (sectionId) {
        const sectionBucketsData = await progressTracker.getSectionBucketBreakdown(
          userId,
          courseId,
          sectionId
        );
        setSectionBuckets(sectionBucketsData);
      }

      // Get tag masteries for detailed view
      const masteries = await tagMasteryCalculator.getAllTagMasteries(userId, courseId);
      setTagMasteries(masteries);
    } catch (err) {
      console.error('[BucketAnalytics] Error loading bucket data:', err);
      setError(`Failed to load analytics: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bucket-analytics loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading analytics...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bucket-analytics error">
        <div className="alert alert-warning">{error}</div>
      </div>
    );
  }

  return (
    <div className="bucket-analytics">
      {/* Section-level breakdown */}
      {sectionId && sectionBuckets && (
        <div className="bucket-section">
          <h4 className="bucket-title">📊 Section Progress</h4>
          <BucketChart buckets={sectionBuckets} level="section" />
          <BucketLegend buckets={sectionBuckets} />
        </div>
      )}

      {/* Course-level breakdown */}
      {courseBuckets && (
        <div className="bucket-section">
          <h4 className="bucket-title">🎓 Course Progress</h4>
          <BucketChart buckets={courseBuckets} level="course" />
          <BucketLegend buckets={courseBuckets} />
        </div>
      )}

      {/* Tag-level breakdown */}
      {tagMasteries && tagMasteries.length > 0 && (
        <div className="bucket-section">
          <h4 className="bucket-title">🏷️ Topic Mastery</h4>
          <TagMasteryList tagMasteries={tagMasteries} />
        </div>
      )}
    </div>
  );
}

/**
 * Visual representation of bucket distribution
 */
function BucketChart({ buckets, level }) {
  const { unseen = 0, weak = 0, review = 0, learned = 0, total = 0 } = buckets;

  if (total === 0) {
    return <div className="alert alert-info">No data available yet.</div>;
  }

  const unSeenPct = (unseen / total) * 100;
  const weakPct = (weak / total) * 100;
  const reviewPct = (review / total) * 100;
  const learnedPct = (learned / total) * 100;

  return (
    <div className="bucket-chart">
      <div className="bucket-bar">
        {unseen > 0 && (
          <div
            className="bucket-segment unseen"
            style={{ width: `${unSeenPct}%` }}
            title={`Unseen: ${unseen} (${unSeenPct.toFixed(1)}%)`}
          >
            {unSeenPct > 5 && <span className="bucket-label">{unseen}</span>}
          </div>
        )}
        {weak > 0 && (
          <div
            className="bucket-segment weak"
            style={{ width: `${weakPct}%` }}
            title={`Weak: ${weak} (${weakPct.toFixed(1)}%)`}
          >
            {weakPct > 5 && <span className="bucket-label">{weak}</span>}
          </div>
        )}
        {review > 0 && (
          <div
            className="bucket-segment review"
            style={{ width: `${reviewPct}%` }}
            title={`Review: ${review} (${reviewPct.toFixed(1)}%)`}
          >
            {reviewPct > 5 && <span className="bucket-label">{review}</span>}
          </div>
        )}
        {learned > 0 && (
          <div
            className="bucket-segment learned"
            style={{ width: `${learnedPct}%` }}
            title={`Learned: ${learned} (${learnedPct.toFixed(1)}%)`}
          >
            {learnedPct > 5 && <span className="bucket-label">{learned}</span>}
          </div>
        )}
      </div>

      {/* Stats below chart */}
      <div className="bucket-stats">
        <div className="stat">
          <span className="stat-value">{total}</span>
          <span className="stat-label">Total Questions</span>
        </div>
        <div className="stat">
          <span className="stat-value" style={{ color: '#ff9800' }}>
            {((learned / total) * 100).toFixed(0)}%
          </span>
          <span className="stat-label">Mastered</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Legend explaining bucket meanings
 */
function BucketLegend({ buckets }) {
  const { unseen = 0, weak = 0, review = 0, learned = 0 } = buckets;

  return (
    <div className="bucket-legend">
      <div className="legend-item">
        <div className="legend-color unseen"></div>
        <div className="legend-text">
          <strong>Unseen ({unseen})</strong>
          <p>Brand new questions (score 0-33%)</p>
        </div>
      </div>
      <div className="legend-item">
        <div className="legend-color weak"></div>
        <div className="legend-text">
          <strong>Weak ({weak})</strong>
          <p>Struggling with topic (score 34-66%)</p>
        </div>
      </div>
      <div className="legend-item">
        <div className="legend-color review"></div>
        <div className="legend-text">
          <strong>Review ({review})</strong>
          <p>Needs spaced repetition (score 67-89%)</p>
        </div>
      </div>
      <div className="legend-item">
        <div className="legend-color learned"></div>
        <div className="legend-text">
          <strong>Learned ({learned})</strong>
          <p>Mastered topic (score 90-100%)</p>
        </div>
      </div>
    </div>
  );
}

/**
 * Display tag-level mastery breakdown
 */
function TagMasteryList({ tagMasteries }) {
  return (
    <div className="tag-mastery-list">
      {tagMasteries && tagMasteries.length > 0 ? (
        tagMasteries.map((tm) => {
          const masteryPercent = toPercent(
            tm?.finalMastery ?? tm?.percentage ?? tm?.mastery ?? tm?.score,
          );
          const decayPercent = toPercent(tm?.decayFactor ?? tm?.decay);
          const tagName = tm?.tagLabel || tm?.tagId || 'Unknown';

          return (
            <div key={tm.tagId || tagName} className="tag-mastery-item">
              <div className="tag-info">
                <h6 className="tag-name">{tagName}</h6>
                <p className="tag-stats">
                  {tm.correctCount || 0}/{tm.totalCount || 0} correct
                  {decayPercent > 0 && ` • Decay: ${decayPercent.toFixed(0)}%`}
                </p>
              </div>
              <div className="tag-mastery">
                <div className="mastery-bar">
                  <div
                    className={`mastery-fill ${getBucketClass(tm.bucket)}`}
                    style={{ width: `${masteryPercent}%` }}
                    title={`${masteryPercent.toFixed(0)}% mastery`}
                  />
                </div>
                <span className="mastery-percentage">
                  {masteryPercent.toFixed(0)}%
                </span>
              </div>
            </div>
          );
        })
      ) : (
        <p className="text-muted">No tag mastery data available yet.</p>
      )}
    </div>
  );
}

function toPercent(value) {
  if (value == null || value === '') return 0;

  const numeric = typeof value === 'string'
    ? Number.parseFloat(value.replace('%', '').trim())
    : Number(value);

  if (!Number.isFinite(numeric)) return 0;

  // Support both 0-1 ratios and 0-100 percentages.
  const percent = numeric >= 0 && numeric <= 1 ? numeric * 100 : numeric;
  return Math.max(0, Math.min(100, percent));
}

/**
 * Helper: get CSS class for bucket
 */
function getBucketClass(bucket) {
  switch (bucket) {
    case 'learned':
      return 'bucket-learned';
    case 'review':
      return 'bucket-review';
    case 'weak':
      return 'bucket-weak';
    case 'unseen':
    default:
      return 'bucket-unseen';
  }
}

export default BucketAnalytics;
